/**
 * CSS Variables Management
 * Handles dynamic CSS variable injection and theme management
 */

export interface StyleVariables {
  // Colors
  theme_color?: string
  button_hover_color?: string
  button_active_color?: string
  button_text_color?: string
  button_shadow_color?: string
  selection_background_color?: string
  action_color?: string
  text_color?: string
  description_text_color?: string
  checkout_text_color?: string
  light_text_color?: string
  sale_original_price_color?: string
  content_background_color?: string
  default_background_color?: string
  secondary_button_color?: string
  // Typography
  font_family?: string
  // Logos
  desktop_logo_top_margin?: string
  desktop_logo_height?: string
  mobile_logo_top_margin?: string
  mobile_logo_bottom_margin?: string
  mobile_logo_height?: string
  // Behaviour
  mobile_icon_button_type?: string
  [key: string]: string | undefined
}

/**
 * Update CSS variables dynamically
 */
export function updateStyleVariables(variables: StyleVariables): void {
  if (typeof document === 'undefined') {
    return
  }

  const processedVariables = { ...variables }

  // Handle theme color fallbacks
  if (processedVariables.theme_color) {
    const colorVariables = ['button_hover_color', 'button_active_color', 'action_color']

    colorVariables.forEach(key => {
      // Set the default color option if it's not specified
      processedVariables[key] = processedVariables[key] || processedVariables.theme_color
    })

    if (!processedVariables.button_shadow_color) {
      processedVariables.button_shadow_color = 'transparent' // default to transparent shadow
    }
  }

  // Handle margin variables
  const marginVariables = ['desktop_logo_top_margin', 'mobile_logo_bottom_margin', 'mobile_logo_top_margin']

  marginVariables.forEach(key => {
    // Don't replace == with ===, that's intentional for string/number comparison

    processedVariables[key] =
      processedVariables[key] == '0' || processedVariables[key] === undefined ? '0px' : processedVariables[key]
  })

  // Apply to CSS custom properties
  const root = document.documentElement

  Object.entries(processedVariables).forEach(([key, value]) => {
    if (value === undefined) {
      return
    }
    if (key.startsWith('--')) {
      root.style.setProperty(key, value)
      return
    }
    const snakeKey = `--${key}`
    const kebabKey = `--${key.replace(/_/g, '-')}`
    root.style.setProperty(snakeKey, value)
    if (kebabKey !== snakeKey) {
      root.style.setProperty(kebabKey, value)
    }
  })

  if (processedVariables.font_family) {
    root.style.setProperty('--font-family-sans', processedVariables.font_family)
  }
}

// Load the campaign-wide stylesheet via <link>. Updates href if the URL
// changes so a different campaign's styles replace the prior one instead
// of getting skipped.
export function appendCampaignStyle(styleUrl: string): void {
  if (typeof document === 'undefined' || !styleUrl) {
    return
  }

  const existing = document.getElementById('campaign_stylesheet') as HTMLLinkElement | null
  if (existing) {
    if (existing.href !== styleUrl) {
      existing.href = styleUrl
    }
    return
  }

  const file = document.createElement('link')
  file.rel = 'stylesheet'
  file.id = 'campaign_stylesheet'
  file.href = styleUrl
  document.head.appendChild(file)
}

// Inject a CSS string as a <style id={id}> element. Replaces any existing
// element with the same id so styles update on session/campaign change.
// Used for backend-supplied builder/header/footer CSS (session.style etc).
export function injectInlineStyles(id: string, css: string | null | undefined): void {
  if (typeof document === 'undefined') {
    return
  }

  const existing = document.getElementById(id)
  if (!css) {
    if (existing) {
      existing.remove()
    }
    return
  }

  if (existing) {
    if (existing.textContent !== css) {
      existing.textContent = css
    }
    return
  }

  const el = document.createElement('style')
  el.id = id
  el.textContent = css
  document.head.appendChild(el)
}

/**
 * Append favicon to document
 */
export function appendFavicon(faviconUrl: string): void {
  if (typeof document === 'undefined' || !faviconUrl) {
    return
  }

  let link = document.getElementById('campaign_favicon') as HTMLLinkElement

  if (link) {
    link.href = faviconUrl
  } else {
    link = document.createElement('link')
    link.type = 'image/png'
    link.rel = 'icon'
    link.id = 'campaign_favicon'
    link.href = faviconUrl
    document.head.appendChild(link)
  }
}

/**
 * Set content width styles
 */
export function setContentWidth(contentConfig: any): void {
  if (typeof document === 'undefined' || !contentConfig) {
    return
  }

  // Add content width styles based on configuration
  const styles = []

  if (contentConfig.desktop_width) {
    styles.push(`
      @media (min-width: 768px) {
        .container {
          max-width: ${contentConfig.desktop_width};
        }
      }
    `)
  }

  if (contentConfig.mobile_width) {
    styles.push(`
      @media (max-width: 767px) {
        .container {
          max-width: ${contentConfig.mobile_width};
        }
      }
    `)
  }

  if (styles.length > 0) {
    const styleElement = document.createElement('style')
    styleElement.id = 'content-width-styles'
    styleElement.textContent = styles.join('\n')
    document.head.appendChild(styleElement)
  }
}
